import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Plus, RefreshCw } from "lucide-react";
import {
  Field,
  ORG_TYPES,
  ORG_TYPE_LABEL,
} from "@/components/client-manager/_shared";
import { LivePreview } from "@/components/client-manager/LivePreview";
import { CreateClientDialog } from "@/components/client-manager/CreateClientDialog";
import { SingleLevelEditor } from "@/components/client-manager/SingleLevelEditor";
import { PubGroupEditor } from "@/components/client-manager/PubGroupEditor";

export default function ClientManagerPage() {
  const [orgType, setOrgType] = useState("pub_group");
  const [clients, setClients] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [livePreview, setLivePreview] = useState({ name: "", town: "", color: "#0038FF", logoUrl: null, slug: null });

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    try {
      const [c, p] = await Promise.all([api.get("/clients"), api.get("/pubs")]);
      setClients(c.data);
      setPubs(p.data);
      setLastRefreshedAt(new Date());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Instant sync (iter69) — whenever the super-admin tabs back to the
  // Client Manager after editing a logo/brand elsewhere (e.g. another
  // tab showing `/c/[slug]/editor`), silently re-fetch so the Read-Only
  // Preview reflects the latest bytes. Also re-fetch when the window
  // regains focus for the same reason.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") load({ silent: true });
    };
    window.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      window.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [load]);

  // Filter clients by the currently selected org type. Records missing a
  // type fall into pub_group (legacy default).
  const filteredClients = useMemo(
    () => clients.filter((c) => (c.org_type || "pub_group") === orgType),
    [clients, orgType],
  );

  // When the org-type filter changes, snap selection to the first client of
  // that type (or clear it).
  useEffect(() => {
    if (filteredClients.length === 0) { setSelectedId(null); return; }
    if (!filteredClients.some((c) => c.id === selectedId)) {
      setSelectedId(filteredClients[0].id);
    }
  }, [orgType, filteredClients, selectedId]);

  const selected = filteredClients.find((c) => c.id === selectedId) || null;
  const selectedPubs = useMemo(
    () => pubs.filter((p) => p.client_id === selectedId),
    [pubs, selectedId],
  );
  // For single-level clients (independent pub / foundation / charity) there
  // is exactly one landing pub — surface it for the QR / slug URL.
  const landingPub = orgType === "pub_group" ? null : selectedPubs[0] || null;

  const handleLive = useCallback((v) => setLivePreview((prev) => ({ ...prev, ...v })), []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F5F5F5" }}>
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="client-manager-page">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 flex items-center gap-2">
              <Building2 className="h-6 w-6 text-zinc-500" /> Client Manager
            </h1>
            <p className="mt-1 text-sm text-zinc-500 max-w-2xl">
              Manage B2B relationships across pub groups, independent venues, football foundations and charities. Each gets its own branded <code className="px-1 bg-zinc-200 rounded-sm text-[11px]">/&lt;slug&gt;</code> landing.
            </p>
          </div>
          <Link to="/admin" className="text-xs uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-950 underline underline-offset-4">
            &larr; Back to Admin
          </Link>
        </div>

        {/* Refresh strip — quick manual pull + timestamp so admins know
            the preview is live. Auto-refetch already runs on tab focus. */}
        <div className="flex items-center justify-between mb-4" data-testid="client-manager-refresh-bar">
          <p className="text-[11px] uppercase tracking-[0.15em] text-zinc-500">
            {lastRefreshedAt ? `Last synced ${lastRefreshedAt.toLocaleTimeString()}` : "Loading…"}
          </p>
          <button
            type="button"
            onClick={() => load({ silent: true })}
            disabled={refreshing || loading}
            data-testid="client-manager-refresh"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-700 hover:text-zinc-950 border border-zinc-200 hover:border-zinc-950 rounded-sm px-3 h-8 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {/* Selectors */}
        <div className="bg-white rounded-md border border-zinc-200 p-5 mb-6 grid grid-cols-1 md:grid-cols-[220px_1fr_auto] gap-4 items-end" data-testid="client-selectors">
          <Field label="Organisation type">
            <Select value={orgType} onValueChange={setOrgType}>
              <SelectTrigger data-testid="org-type-select" className="h-10 rounded-sm border-zinc-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ORG_TYPES.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`org-type-option-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={`Select a ${ORG_TYPE_LABEL[orgType]}`}>
            <Select value={selectedId || ""} onValueChange={(v) => setSelectedId(v || null)} disabled={filteredClients.length === 0}>
              <SelectTrigger data-testid="client-select" className="h-10 rounded-sm border-zinc-300">
                <SelectValue placeholder={loading ? "Loading…" : filteredClients.length === 0 ? `No ${ORG_TYPE_LABEL[orgType]}s yet` : "Pick a record"} />
              </SelectTrigger>
              <SelectContent>
                {filteredClients.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`client-option-${c.id}`}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Button type="button" onClick={() => setCreateOpen(true)} data-testid="create-client-open" className="rounded-sm bg-zinc-950 hover:bg-black text-white h-10 px-5">
            <Plus className="h-4 w-4 mr-1.5" /> New {ORG_TYPE_LABEL[orgType]}
          </Button>
        </div>

        {/* Main two-section area */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6">
          <div className="space-y-6" data-testid="client-editor-column">
            {!selected ? (
              <div className="bg-white rounded-md border border-zinc-200 p-10 text-center text-sm text-zinc-500" data-testid="empty-state">
                {filteredClients.length === 0
                  ? `Create your first ${ORG_TYPE_LABEL[orgType]} to get started.`
                  : `Pick a ${ORG_TYPE_LABEL[orgType]} above to edit.`}
              </div>
            ) : orgType === "pub_group" ? (
              <PubGroupEditor
                key={selected.id}
                client={selected}
                pubs={selectedPubs}
                onSaved={load}
                onDeleted={load}
                onReloadPubs={load}
                onLive={handleLive}
              />
            ) : (
              <SingleLevelEditor
                key={selected.id}
                client={selected}
                pub={landingPub}
                pubGroups={clients.filter((c) => (c.org_type || "pub_group") === "pub_group")}
                onSaved={load}
                onDeleted={load}
                onLive={handleLive}
              />
            )}
          </div>
          <aside className="hidden lg:block" data-testid="live-preview-column">
            <LivePreview
              name={livePreview.name}
              town={livePreview.town}
              color={livePreview.color}
              logoUrl={livePreview.logoUrl}
              slug={livePreview.slug}
            />
          </aside>
        </div>

        <CreateClientDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          orgType={orgType}
          onCreated={async (created) => { await load(); if (created?.id) setSelectedId(created.id); }}
        />
      </main>
    </div>
  );
}
